import React, { useCallback, useEffect, useState } from 'react';
import { FormControlLabel, Switch, Paper, Typography, Stack } from '@mui/material';
import { featureToggleService } from '../services/api';

// Reusable admin on/off switch for a backend feature toggle (see FeatureTogglesController).
// Shared by GoogleMeetAdmin and LiveKitAdmin so both integrations get the same runtime
// enable/disable control without duplicating the fetch/save logic.
interface FeatureToggleSwitchProps {
  featureKey: string;
  label: string;
  description?: string;
}

const FeatureToggleSwitch: React.FC<FeatureToggleSwitchProps> = ({ featureKey, label, description }) => {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const all = await featureToggleService.getAll();
      const entry = all.find(t => t.featureKey === featureKey);
      setEnabled(entry?.enabled ?? false);
    } catch {
      setEnabled(null);
    }
  }, [featureKey]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSaving(true);
    // Optimistic update — revert to actual server state if the save fails.
    setEnabled(checked);
    try {
      await featureToggleService.setEnabled(featureKey, checked);
    } catch {
      load();
    } finally {
      setSaving(false);
    }
  };

  if (enabled === null) return null; // still loading, or admin route not reachable — render nothing rather than a flash of wrong state

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Stack>
          <Typography variant="subtitle1" fontWeight={600}>{label}</Typography>
          {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
        </Stack>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={handleToggle} disabled={saving} color="success" />}
          label={enabled ? 'Enabled' : 'Disabled'}
        />
      </Stack>
    </Paper>
  );
};

export default FeatureToggleSwitch;
